// Public landing page (issue #19). Signed-out visitors see the pitch;
// signed-in parents get routed into their kit library instead.
// Copy frames the ritual per #19: "Snap the list. Get the kit."
// Launch video slot is a deliberate placeholder until pilot footage lands.
import { Link } from "react-router-dom";
import { useAuth } from "../auth";
import { track, EVENTS } from "../analytics";

const PLANS = [
  {
    name: "Free", price: "$0", blurb: "One full kit — the magic trick, on us.",
    features: ["1 kit, all five modules", "Print-ready PDFs", "1 kid"], cta: false,
  },
  {
    name: "Family", price: "$6.99/mo", yearly: "$49/yr — save ~41%",
    blurb: "Every weekly list, all year.", features: ["Unlimited kits", "1 kid", "A fresh booklet every week"], cta: true, featured: true,
  },
  {
    name: "Family+", price: "$9.99/mo",
    blurb: "The whole crew.", features: ["Unlimited kits", "Up to 4 kids", "A fresh booklet every week"], cta: true,
  },
];

export default function Landing() {
  const { uid, loading } = useAuth();
  const authed = !loading && !!uid;

  return (
    <div className="landing">
      <header className="topbar">
        <span className="brand">VocabKit</span>
        <nav>
          {authed
            ? <Link to="/app">My kits</Link>
            : <><a href="#plans">Plans</a><Link to="/signin">Sign in</Link></>}
        </nav>
      </header>

      <section className="hero">
        <h1>Snap the list.<br />Get the kit.</h1>
        <p className="lede">
          Photograph the vocabulary list from your kid&apos;s reading group — the
          spelling words, the science terms, the wall of new words — and get a
          full week of print-ready practice back in about a minute.
        </p>
        <div className="hero-cta">
          {authed ? (
            <Link className="big-cta" to="/app">Open your kit library</Link>
          ) : (
            <Link className="big-cta" to="/signin" onClick={() => track(EVENTS.landingCta, { where: "hero" })}>
              Get your first kit free
            </Link>
          )}
          <span className="muted">No passwords — a sign-in link finds you by email.</span>
        </div>
        <div className="video-slot" aria-label="VocabKit launch video">
          <video controls playsInline preload="metadata" poster="/launch_poster.jpg" src="/launch.mp4"></video>
        </div>
      </section>

      <section className="steps">
        <h2>How it works</h2>
        <ol>
          <li>
            <strong>1. Snap the list.</strong> Two or three photos of any word
            list — a page, a whiteboard, a hand-written chart.
          </li>
          <li>
            <strong>2. Check the words.</strong> We read the list; you confirm
            or fix it. Nothing prints until you approve.
          </li>
          <li>
            <strong>3. Print the kit.</strong> Five activities arrive as
            PDFs, ready for the printer and a pencil.
          </li>
        </ol>
      </section>

      <section className="modules">
        <h2>Every kit, five ways to practice</h2>
        <ul>
          <li><strong>Flashcards</strong> — cut-out cards with each word and its group.</li>
          <li><strong>Word search</strong> — the week&apos;s words, hiding in plain sight.</li>
          <li><strong>Crossword</strong> — clues built from your kid&apos;s own list.</li>
          <li><strong>Handwriting practice</strong> — trace, then write, every word.</li>
          <li><strong>Weekly booklet</strong> — all of it stapled into one take-apart pack.</li>
        </ul>
      </section>

      <section className="plans" id="plans">
        <h2>Plans</h2>
        <div className="plans-grid">
          {PLANS.map((p) => (
            <div className={"card plan" + (p.featured ? " featured" : "")} key={p.name}>
              <h3>{p.name}</h3>
              <p className="plan-price">{p.price}</p>
              {p.yearly && <p className="muted">{p.yearly}</p>}
              <p>{p.blurb}</p>
              <ul>{p.features.map((f) => <li key={f}>{f}</li>)}</ul>
              {p.cta && !authed && (
                <Link to="/signin" onClick={() => track(EVENTS.landingCta, { where: `plan:${p.name}` })}>
                  Start free, then upgrade
                </Link>
              )}
            </div>
          ))}
        </div>
        <p className="muted">Your free kit is yours forever — whatever plan you choose after.</p>
      </section>

      <section className="parents">
        <h2>Built for parents, careful with kids</h2>
        <p>
          Accounts belong to grown-ups. There is no kid login, no kid-facing
          screen, and no child data collected beyond the word lists you choose
          to photograph. Photos of your lists are used only to build your kits.
        </p>
      </section>

      <section className="closing-cta">
        <h2>Ready when the next list comes home.</h2>
        {authed ? (
          <Link className="big-cta" to="/app">Open your kit library</Link>
        ) : (
          <Link className="big-cta" to="/signin" onClick={() => track(EVENTS.landingCta, { where: "footer" })}>
            Get your first kit free
          </Link>
        )}
      </section>

      <footer className="footer muted">
        VocabKit · weekly vocabulary kits your kid actually asks for ·{" "}
        <Link to="/legal/privacy">Privacy</Link> · <Link to="/legal/terms">Terms</Link>
      </footer>
    </div>
  );
}
